import { useEffect, useRef, useState } from 'react';
import {
  macroTargets,
  basalMetabolicRate,
  waterGoalMl,
  type ActivityLevel,
  type GoalType,
  type ProfileInput,
  type Sex,
  type User,
} from '@calorie/shared';
import { haptic } from '../lib/telegram.js';
import { getThemePreference, saveThemePreference, type ThemePreference } from '../lib/theme.js';

const ACTIVITY_OPTIONS: Record<ActivityLevel, string> = {
  sedentary: 'Сидячий образ жизни',
  light: 'Лёгкая, 1–2 тренировки',
  moderate: 'Умеренная, 3–4 тренировки',
  high: 'Высокая, 5–6 тренировок',
  athlete: 'Спортсмен, тяжёлый труд',
};

const ACTIVITY_SHORT: Record<ActivityLevel, string> = {
  sedentary: 'Сидячий',
  light: 'Лёгкая',
  moderate: 'Умеренная',
  high: 'Высокая',
  athlete: 'Спортсмен',
};

const GOAL_LABELS: Record<GoalType, string> = {
  lose: 'Похудеть',
  maintain: 'Держать',
  gain: 'Набрать',
};

const AUTOSAVE_DELAY_MS = 800;
const WEIGHT_STEP = 0.1;
const WEIGHT_MIN = 25;
const WEIGHT_MAX = 400;

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface Props {
  user: User;
  initial?: ProfileInput | null;
  onSave: (input: ProfileInput) => Promise<void>;
  onUpdateName: (firstName: string) => Promise<User>;
  onBack: () => void;
  onWeightDetails: () => void;
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const index = options.findIndex((o) => o.value === value);
  return (
    <div className="seg-ios" style={{ '--count': options.length } as React.CSSProperties}>
      <span
        className="seg-ios-pill"
        style={{ transform: `translateX(${index * 100}%)` }}
        aria-hidden="true"
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="seg-ios-item"
          aria-pressed={o.value === value}
          onClick={() => {
            haptic();
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function clampWeight(n: number) {
  return Math.min(WEIGHT_MAX, Math.max(WEIGHT_MIN, Math.round(n * 10) / 10));
}

/**
 * Настройки в духе iOS 26: стекло, крупные группы, имя и вес на виду.
 *
 * Правки уходят сами после паузы. Недописанное число не сохраняется.
 */
export function Settings({ user, initial, onSave, onUpdateName, onBack, onWeightDetails }: Props) {
  const [theme, setTheme] = useState<ThemePreference>(getThemePreference);
  useEffect(() => {
    const update = () => setTheme(getThemePreference());
    window.addEventListener('cora:theme-preference', update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener('cora:theme-preference', update);
      window.removeEventListener('storage', update);
    };
  }, []);
  const [name, setName] = useState(user.firstName ?? '');
  const [sex, setSex] = useState<Sex>(initial?.sex ?? 'male');
  const [age, setAge] = useState(String(initial?.ageYears ?? 30));
  const [height, setHeight] = useState(String(initial?.heightCm ?? 175));
  const [weight, setWeight] = useState(String(initial?.weightKg ?? 70));
  const [activity, setActivity] = useState<ActivityLevel>(
    initial?.activityLevel ?? 'light',
  );
  const [goal, setGoal] = useState<GoalType>(initial?.goalType ?? 'maintain');
  const [waterTracking, setWaterTracking] = useState(initial?.waterTrackingEnabled ?? false);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const draft: ProfileInput = {
    sex,
    ageYears: Number(age) || 0,
    heightCm: Number(height) || 0,
    weightKg: Number(weight) || 0,
    activityLevel: activity,
    goalType: goal,
    waterTrackingEnabled: waterTracking,
  };

  const valid =
    draft.ageYears >= 10 &&
    draft.ageYears <= 120 &&
    draft.heightCm >= 80 &&
    draft.weightKg >= WEIGHT_MIN;

  const preview = valid ? macroTargets(draft) : null;
  const restingKcal = valid && draft.ageYears >= 18 ? Math.round(basalMetabolicRate(draft)) : null;
  const waterGoal = valid ? waterGoalMl(draft) : null;
  const nickname = user.username ? `@${user.username}` : 'не указан';

  const values = JSON.stringify(draft);
  const initialValues = useRef(values);
  const initialName = useRef((user.firstName ?? '').trim());

  useEffect(() => {
    if (values === initialValues.current) return;
    if (!valid) return;

    const timer = setTimeout(async () => {
      setSaveState('saving');
      try {
        await onSave(JSON.parse(values) as ProfileInput);
        initialValues.current = values;
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [values, valid, onSave]);

  useEffect(() => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === initialName.current) return;

    const timer = setTimeout(async () => {
      setSaveState('saving');
      try {
        const updated = await onUpdateName(trimmed);
        initialName.current = updated.firstName ?? trimmed;
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [name, onUpdateName]);

  function bumpWeight(delta: number) {
    haptic();
    const next = clampWeight((Number(weight) || 0) + delta);
    setWeight(String(next));
  }

  return (
    <div className="app settings">
      <header className="settings-nav">
        <button className="nav-back" onClick={onBack}>
          <Chevron className="nav-back-icon" />
          Дневник
        </button>
        <span className={`nav-state ${saveState}`} role="status">
          {saveState === 'saving' && 'Сохраняем…'}
          {saveState === 'saved' && 'Сохранено'}
          {saveState === 'error' && 'Не сохранилось'}
        </span>
      </header>

      <h1 className="large-title">Настройки</h1>

      <p className="group-caption">Оформление</p>
      <div className="group glass theme-settings" role="group" aria-label="Тема оформления">
        <div className="theme-settings-title">
          <span className="theme-icon" aria-hidden="true">◐</span>
          <span>Тема приложения</span>
        </div>
        <Segmented<ThemePreference>
          value={theme}
          onChange={saveThemePreference}
          options={[
            { value: 'system', label: 'Системная' },
            { value: 'light', label: 'Светлая' },
            { value: 'dark', label: 'Тёмная' },
          ]}
        />
      </div>
      <p className="group-footer">Системная — как в Telegram, а в браузере — как на устройстве. Выбор сохраняется на этом устройстве.</p>

      <p className="group-caption">Профиль</p>
      <div className="group glass">
        <div className="cell">
          <label className="cell-label" htmlFor="display-name">Имя</label>
          <input
            id="display-name"
            className="cell-input cell-input-text"
            type="text"
            autoComplete="given-name"
            enterKeyHint="done"
            maxLength={64}
            placeholder="Как к вам обращаться"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="cell">
          <span className="cell-label">Никнейм в Телеграмм</span>
          <span className={`cell-value ${user.username ? 'cell-value-brand' : 'cell-value-muted'}`}>
            {nickname}
          </span>
        </div>
      </div>
      <p className="group-caption">Вес</p>
      <div className="group glass weight-card">
        <p className="weight-card-kicker">Текущий вес</p>
        <div className="weight-hero">
          <input
            id="weight"
            className="weight-input"
            type="number"
            inputMode="decimal"
            step={WEIGHT_STEP}
            min={WEIGHT_MIN}
            max={WEIGHT_MAX}
            aria-label="Вес в килограммах"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            /* Ширина по содержимому: при фиксированной ширины поля короткое
               число прижималось вправо, и «91 кг» стояло правее центра */
            style={{ width: `${Math.max(weight.length, 2)}ch` }}
          />
          <span className="weight-unit">кг</span>
        </div>
        <div className="weight-stepper" role="group" aria-label="Изменить вес">
          <button type="button" onClick={() => bumpWeight(-WEIGHT_STEP)} aria-label="Минус 0,1 кг">
            −
          </button>
          <button type="button" onClick={() => bumpWeight(WEIGHT_STEP)} aria-label="Плюс 0,1 кг">
            +
          </button>
        </div>
        <button className="weight-details-link" type="button" disabled={saveState === 'saving' || values !== initialValues.current}
          onClick={() => { haptic(); onWeightDetails(); }}>
          Подробнее <Chevron className="cell-chevron" />
        </button>
      </div>
      {!valid && Number(weight) > 0 && Number(weight) < WEIGHT_MIN ? (
        <p className="group-footer warning">Вес от {WEIGHT_MIN} кг - пока не сохраняю.</p>
      ) : (
        <p className="group-footer">
          От веса считается норма калорий и белка. Изменение пишется в историю за сегодня.
        </p>
      )}

      <p className="group-caption">Вода</p>
      <div className="group glass">
        <div className="cell">
          <span className="cell-label">Учёт воды</span>
          <button className={`ios-switch${waterTracking ? ' on' : ''}`} type="button" role="switch"
            aria-checked={waterTracking} aria-label="Учёт воды"
            onClick={() => { haptic(); setWaterTracking((value) => !value); }}>
            <span />
          </button>
        </div>
        {waterTracking && waterGoal != null && <div className="cell">
          <span className="cell-label">Ориентир на день</span>
          <span className="cell-value cell-value-brand">{waterGoal.toLocaleString('ru-RU')} мл</span>
        </div>}
      </div>
      <p className="group-footer">
        {waterTracking
          ? 'Рассчитываем ориентир по весу. «Стакан воды» добавит 250 мл в выбранный день.'
          : 'Включите, чтобы видеть кольцо прогресса и записывать воду текстом.'}
      </p>

      <p className="group-caption">Данные о себе</p>
      <div className="group glass">
        <div className="cell">
          <span className="cell-label">Пол</span>
          <Segmented
            value={sex}
            onChange={setSex}
            options={[
              { value: 'male' as Sex, label: 'Мужской' },
              { value: 'female' as Sex, label: 'Женский' },
            ]}
          />
        </div>

        <div className="cell">
          <label className="cell-label" htmlFor="age">Возраст</label>
          <span className="cell-value">
            <input id="age" className="cell-input" type="number" inputMode="numeric"
                   value={age} onChange={(e) => setAge(e.target.value)} />
            <span className="cell-unit">лет</span>
          </span>
        </div>

        <div className="cell">
          <label className="cell-label" htmlFor="height">Рост</label>
          <span className="cell-value">
            <input id="height" className="cell-input" type="number" inputMode="numeric"
                   value={height} onChange={(e) => setHeight(e.target.value)} />
            <span className="cell-unit">см</span>
          </span>
        </div>
      </div>
      {!valid && (
        <p className="group-footer warning">
          Проверьте возраст, рост и вес - пока не сохраняю.
        </p>
      )}

      <p className="group-caption">Образ жизни</p>
      <div className="group glass">
        <div className="cell cell-tappable">
          <span className="cell-label">Активность</span>
          <span className="cell-value cell-value-muted">
            {ACTIVITY_SHORT[activity]}
            <Chevron className="cell-chevron" />
          </span>
          <select
            className="cell-select"
            value={activity}
            aria-label="Активность"
            onChange={(e) => setActivity(e.target.value as ActivityLevel)}
          >
            {Object.entries(ACTIVITY_OPTIONS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <div className="cell">
          <span className="cell-label">Цель</span>
          <Segmented
            value={goal}
            onChange={setGoal}
            options={(Object.keys(GOAL_LABELS) as GoalType[]).map((g) => ({
              value: g,
              label: GOAL_LABELS[g],
            }))}
          />
        </div>
      </div>

      {preview && (
        <>
          <p className="group-caption">Ваша норма</p>
          <div className="group glass">
            <div className="cell">
              <span className="cell-label">Калории</span>
              <span className="cell-value cell-value-strong">{preview.kcal} ккал</span>
            </div>
            {restingKcal != null && <div className="cell">
              <span className="cell-label">Базовый обмен</span>
              <span className="cell-value cell-value-muted">≈ {restingKcal} ккал/день</span>
            </div>}
            <div className="cell">
              <span className="cell-label">Белки</span>
              <span className="cell-value cell-value-muted">{preview.proteinG} г</span>
            </div>
            <div className="cell">
              <span className="cell-label">Жиры</span>
              <span className="cell-value cell-value-muted">{preview.fatG} г</span>
            </div>
            <div className="cell">
              <span className="cell-label">Углеводы</span>
              <span className="cell-value cell-value-muted">{preview.carbsG} г</span>
            </div>
          </div>
        </>
      )}

      <p className="group-footer">
        Норма считается по формуле Миффлина - Сан Жеора. Изменения сохраняются автоматически.
      </p>
      {restingKcal != null && <p className="group-footer">
        Базовый обмен — примерный расход энергии в покое, а не минимальная норма питания.
        Повседневная активность требует дополнительной энергии; индивидуальную нижнюю границу питания определяют со специалистом.
      </p>}
    </div>
  );
}
